/**
 * Jagad Jawa — Modul Domain: Jodoh UI (Tahap 4)
 * Pengendali DOM untuk formulir input weton & aksara mempelai,
 * visual score proportion bar, rekomendasi 5 tanggal mantu rahayu,
 * riwayat hitungan pasangan (LocalStorage), dan laporan cetak Kasultanan.
 */

import {
  AKSARA_PERJODOHAN,
  autoDetectAksara,
  hitungPitung7Metode,
  cariRekomendasiTanggalMantu,
  getTingkatKeharmonisan,
  DISCLAIMER_ETIS_PERJODOHAN
} from './jodoh-engine.js';
import {
  saveJodohHistory,
  getJodohHistory,
  deleteJodohHistoryItem,
  clearAllJodohHistory
} from './jodoh-history.js';
import { saveBookmark } from '../kalender/bookmark-service.js';
import { HARI, PASARAN, NEPTU_HARI, NEPTU_PASARAN, getDayInfo } from '../kalender/kalender-engine.js';
import { showToast } from '../../ui/toast.js';
import { loadDomainData } from '../../services/dbLoader.js';
import { renderDataErrorState } from '../../ui/data-error-state.js';

export function initPerjodohanSelects() {
  loadDomainData('jodoh').catch((e) => {
    console.warn('[jodoh-ui] loadDomainData fallback:', e);
  });
  const selects = ['aksaraDepanP', 'aksaraBelakangP', 'aksaraDepanL', 'aksaraBelakangL'];
  selects.forEach(id => {
    const sel = document.getElementById(id);
    if (!sel) return;
    sel.innerHTML = '';
    AKSARA_PERJODOHAN.forEach(a => {
      const opt = document.createElement('option');
      opt.value = a.kode;
      opt.textContent = `${a.kode} (IV:${a.iv}|V:${a.vvi})`;
      sel.appendChild(opt);
    });
  });

  ['P', 'L'].forEach(side => {
    const hSel = document.getElementById('hari' + side);
    const pSel = document.getElementById('pasaran' + side);
    if (!hSel || !pSel) return;
    hSel.innerHTML = ''; pSel.innerHTML = '';
    HARI.forEach(h => hSel.appendChild(new Option(h, h)));
    PASARAN.forEach(p => pSel.appendChild(new Option(p, p)));
  });

  // Pasang listener reaktif untuk input nama lengkap (Calon Pengantin Pria & Wanita)
  ['P', 'L'].forEach(side => {
    const elNama = document.getElementById('nama' + side);
    if (elNama) {
      const handleNameInput = () => {
        autoDetectAksaraUI(side);
      };
      elNama.addEventListener('input', handleNameInput);
      elNama.addEventListener('keyup', handleNameInput);
      elNama.addEventListener('change', handleNameInput);
      if (elNama.value && elNama.value.trim()) {
        autoDetectAksaraUI(side);
      }
    }
  });

  // Pasang listener status hubungan (Belum Menikah vs Sudah Menikah)
  initStatusHubunganPerjodohanEvents();

  // Render riwayat perhitungan awal
  renderRiwayatPerjodohan();
}

/**
 * State wizard aktif untuk formulir Perjodohan (1..4)
 */
let jodohCurrentStep = 1;

/**
 * Normalisasi nilai status hubungan
 * @param {string} status
 * @returns {'planning'|'engaged'|'married'}
 */
export function normalizeStatusHubungan(status) {
  if (!status) return 'planning';
  if (status === 'sudah_menikah' || status === 'married') return 'married';
  if (status === 'engaged') return 'engaged';
  return 'planning';
}

/**
 * Memeriksa apakah status hubungan tergolong sudah menikah
 * @param {string} status
 * @returns {boolean}
 */
export function isStatusMarried(status) {
  return normalizeStatusHubungan(status) === 'married';
}

/**
 * Dapatkan label status hubungan dwibahasa
 * @param {string} status
 * @returns {string}
 */
export function getStatusHubunganLabel(status) {
  const norm = normalizeStatusHubungan(status);
  const lang = (typeof window !== 'undefined' && typeof window.getLanguage === 'function')
    ? window.getLanguage()
    : 'id';
  const isJv = (lang === 'jv');

  if (norm === 'married') {
    return isJv ? 'Sampun Palakrama (Pasangan)' : 'Sudah Menikah (Pasutri)';
  }
  if (norm === 'engaged') {
    return isJv ? 'Lamaran / Pacangan' : 'Lamaran / Tunangan';
  }
  return isJv ? 'Rancangan Palakrama (Calon Pengantèn)' : 'Rencana Menikah (Calon Pengantin)';
}

/**
 * Handle perubahan radio button status hubungan secara real-time
 * @param {'planning'|'engaged'|'married'|'belum_menikah'|'sudah_menikah'} newStatus
 */
export function onStatusHubunganChange(newStatus) {
  const normalized = normalizeStatusHubungan(newStatus);
  window.LAST_STATUS_HUBUNGAN = normalized;

  if (window.LAST_PERJODOHAN_DATA) {
    if (normalized === 'married') {
      window.LAST_MANTU_DATA = [];
      renderPanduanKeharmonisanPasutri(window.LAST_PERJODOHAN_DATA);
    } else {
      const mantu = cariRekomendasiTanggalMantu(window.LAST_PERJODOHAN_DATA.totalNeptu);
      window.LAST_MANTU_DATA = mantu;
      renderRekomendasiMantu(mantu);
    }
    const pairInfo = document.getElementById('perjodohanPairInfo');
    if (pairInfo) {
      const statusLabel = getStatusHubunganLabel(normalized);
      const p = window.LAST_PERJODOHAN_DATA.pria;
      const w = window.LAST_PERJODOHAN_DATA.wanita;
      pairInfo.innerHTML = `${w.nama} (${w.neptu}) &middot; ${p.nama} (${p.neptu}) &bull; Jumlah Neptu: <strong class="text-prada font-mono">${window.LAST_PERJODOHAN_DATA.totalNeptu}</strong> &bull; Status: <span class="text-amber-300 font-semibold">${statusLabel}</span>`;
    }
  }
}

/**
 * Inisialisasi event listener radio status hubungan (mencegah duplikasi listener)
 */
let isStatusHubunganEventsBound = false;
export function initStatusHubunganPerjodohanEvents() {
  if (typeof document === 'undefined') return;
  if (isStatusHubunganEventsBound) return;

  const radios = document.querySelectorAll('input[name="statusHubunganPerjodohan"]');
  radios.forEach(radio => {
    radio.addEventListener('change', () => {
      onStatusHubunganChange(radio.value);
    });
  });
  isStatusHubunganEventsBound = true;
}

/**
 * Mengatur langkah wizard formulir Perjodohan (1..4) dengan validasi
 * Langkah 1: Status Hubungan Pasangan
 * Langkah 2: Data Calon Pengantin Wanita (Tanggal Lahir wajib)
 * Langkah 3: Data Calon Pengantin Pria (Tanggal Lahir wajib)
 * Langkah 4: Ringkasan Hasil Nujum (Hanya dapat diakses jika kedua tanggal valid)
 *
 * @param {number} targetStep
 * @param {boolean} [validateCurrent=true]
 * @returns {boolean} Sukses berpindah atau terblokir validasi
 */
export function setJodohWizardStep(targetStep, validateCurrent = true) {
  const step = Math.max(1, Math.min(4, parseInt(targetStep, 10) || 1));
  const tglL = document.getElementById('tglL')?.value || '';
  const tglP = document.getElementById('tglP')?.value || '';

  // Validasi saat maju ke langkah berikutnya:
  // Step 1 -> 2: Bebas
  // Step 2 -> 3: Wajib tanggal lahir pria
  // Step 3 -> 4: Wajib tanggal lahir pria & wanita
  if (validateCurrent && step > jodohCurrentStep) {
    if (step >= 3 && !tglL) {
      showToast('Isi tanggal lahir calon pengantin pria terlebih dahulu.');
      const inputL = document.getElementById('tglL');
      if (inputL) inputL.focus();
      return false;
    }
    if (step >= 4) {
      if (!tglL) {
        showToast('Isi tanggal lahir calon pengantin pria terlebih dahulu.');
        const inputL = document.getElementById('tglL');
        if (inputL) inputL.focus();
        return false;
      }
      if (!tglP) {
        showToast('Isi tanggal lahir calon pengantin wanita terlebih dahulu.');
        const inputP = document.getElementById('tglP');
        if (inputP) inputP.focus();
        return false;
      }
      // Otomatis hitung jika belum dihitung atau data baru
      if (!window.LAST_PERJODOHAN_DATA) {
        hitungNujumPerjodohan();
      }
    }
  }

  jodohCurrentStep = step;
  applyJodohWizardStepUI(step);
  return true;
}

/**
 * Terapkan visibilitas UI dan state tombol stepper
 * Mode Stepper Bertahap: Tampilkan hanya langkah aktif secara bersih
 * @param {number} step
 */
export function applyJodohWizardStepUI(step) {
  if (typeof document === 'undefined') return;

  const stepContainer1 = document.getElementById('jodohStepContainer1');
  const step1 = document.getElementById('jodohStep1');
  const step2 = document.getElementById('jodohStep2');
  const formsWrapper = document.getElementById('jodohFormsWrapper');
  const cardBox = document.getElementById('hasilPerjodohanCard');
  const emptyBox = document.getElementById('emptyPerjodohanBox');
  const stepIndicator = document.getElementById('jodohStepIndicator');
  const stepBadge = document.getElementById('jodohStepBadge');
  const progressBar = document.getElementById('jodohProgressBar');
  const progressContainer = document.getElementById('jodohProgressBarContainer');
  const actionButtons = document.getElementById('jodohActionButtons');

  const lang = (typeof window !== 'undefined' && typeof window.getLanguage === 'function')
    ? window.getLanguage()
    : 'id';
  const isJv = (lang === 'jv');

  // Perbarui progress bar (25% -> 50% -> 75% -> 100%)
  const pct = step === 1 ? '25%' : (step === 2 ? '50%' : (step === 3 ? '75%' : '100%'));
  if (progressBar) progressBar.style.width = pct;
  if (progressContainer) progressContainer.setAttribute('aria-valuenow', String(step));
  if (stepBadge) stepBadge.textContent = String(step);

  // Perbarui teks indikator langkah
  if (stepIndicator) {
    let stepText = '';
    if (step === 1) {
      stepText = isJv ? 'Langkah 1 saking 4: Status Sesambetan Pasangan' : 'Langkah 1 dari 4: Status Hubungan Pasangan';
    } else if (step === 2) {
      stepText = isJv ? 'Langkah 2 saking 4: Data Calon Pengantèn Kakung' : 'Langkah 2 dari 4: Data Calon Pengantin Pria (♂)';
    } else if (step === 3) {
      stepText = isJv ? 'Langkah 3 saking 4: Data Calon Pengantèn Putri' : 'Langkah 3 dari 4: Data Calon Pengantin Wanita (♀)';
    } else {
      stepText = isJv ? 'Langkah 4 saking 4: Ringkesan Asil & 7 Metode Pitung' : 'Langkah 4 dari 4: Ringkasan Hasil & 7 Metode Pitung';
    }
    stepIndicator.textContent = stepText;
  }

  // Perbarui highlight tombol tab navigasi (1..4)
  for (let i = 1; i <= 4; i++) {
    const btn = document.getElementById(`jodohNavBtn${i}`);
    if (btn) {
      if (i === step) {
        btn.classList.add('border-prada', 'text-prada', 'bg-sogan-950');
        btn.classList.remove('border-sogan-700', 'text-sogan-300');
        btn.setAttribute('aria-current', 'step');
      } else {
        btn.classList.remove('border-prada', 'text-prada', 'bg-sogan-950');
        btn.classList.add('border-sogan-700', 'text-sogan-300');
        btn.removeAttribute('aria-current');
      }
    }
  }

  // Sembunyikan floating hitung button karena aksi terintegrasi di dalam stepper
  if (actionButtons) actionButtons.classList.add('hidden');

  // Pengaturan visibilitas stepper bertahap yang ketat (1 halaman per langkah)
  if (stepContainer1) stepContainer1.classList.toggle('hidden', step !== 1);
  if (formsWrapper) formsWrapper.classList.toggle('hidden', step !== 2 && step !== 3);
  if (step2) step2.classList.toggle('hidden', step !== 2); // Step 2: Calon Pengantin Pria
  if (step1) step1.classList.toggle('hidden', step !== 3); // Step 3: Calon Pengantin Wanita

  if (step === 4) {
    if (window.LAST_PERJODOHAN_DATA) {
      if (cardBox) cardBox.classList.remove('hidden');
      if (emptyBox) emptyBox.classList.add('hidden');
    } else {
      if (cardBox) cardBox.classList.add('hidden');
      if (emptyBox) emptyBox.classList.remove('hidden');
    }
    if (typeof cardBox?.scrollIntoView === 'function') {
      cardBox.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  } else {
    if (cardBox) cardBox.classList.add('hidden');
    if (emptyBox) emptyBox.classList.add('hidden');
  }
}

export function toggleAccordion7Metode() {
  const content = document.getElementById('rincian7MetodeContent');
  const btn = document.getElementById('btnToggleAccordion7Metode');
  const icon = document.getElementById('iconAccordion7Metode');
  if (!content || !btn) return;

  const isHidden = content.classList.contains('hidden');
  if (isHidden) {
    content.classList.remove('hidden');
    btn.setAttribute('aria-expanded', 'true');
    if (icon) icon.style.transform = 'rotate(180deg)';
  } else {
    content.classList.add('hidden');
    btn.setAttribute('aria-expanded', 'false');
    if (icon) icon.style.transform = 'rotate(0deg)';
  }
}

/**
 * Navigasi CTA ke Tab Ijab dari Rekomendasi Mantu
 * @param {string} [tglStr] Tanggal rekomendasi yang dipilih
 */
export function navigateToIjabDariMantu(tglStr = '') {
  if (typeof window !== 'undefined' && typeof window.switchTab === 'function') {
    window.switchTab('ijab');
  }
  if (tglStr) {
    const ijabInput = document.getElementById('tglIjabInput') || document.getElementById('ijabTanggal');
    if (ijabInput) {
      ijabInput.value = tglStr;
      if (typeof window.syncIjabDariTanggal === 'function') {
        window.syncIjabDariTanggal();
      }
    }
  }
}

export function autoDetectAksaraUI(side) {
  const elNama = document.getElementById('nama' + side);
  if (!elNama) return;
  const nama = elNama.value.trim();
  const elDepan = document.getElementById('aksaraDepan' + side);
  const elBelakang = document.getElementById('aksaraBelakang' + side);
  if (!nama) return;

  const res = autoDetectAksara(nama);
  if (elDepan && res.depan) {
    elDepan.value = res.depan;
    elDepan.dispatchEvent(new Event('change', { bubbles: true }));
  }
  if (elBelakang && res.belakang) {
    elBelakang.value = res.belakang;
    elBelakang.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

export function autoWetonPerjodohan(side) {
  const elTgl = document.getElementById('tgl' + side);
  const badge = document.getElementById('neptu' + side + 'Badge');
  if (!elTgl || !elTgl.value) {
    if (badge) badge.innerText = '-';
    return;
  }
  const [y, m, d] = elTgl.value.split('-').map(Number);
  if (isNaN(y) || isNaN(m) || isNaN(d)) return;

  const info = getDayInfo(y, m, d);
  const elHari = document.getElementById('hari' + side);
  const elPasaran = document.getElementById('pasaran' + side);
  if (elHari) elHari.value = HARI[info.weekdayId];
  if (elPasaran) elPasaran.value = PASARAN[info.pasaranId];
  updateNeptuPerjodohan(side);
}

export function updateNeptuPerjodohan(side) {
  const elHari = document.getElementById('hari' + side);
  const elPasaran = document.getElementById('pasaran' + side);
  const badge = document.getElementById('neptu' + side + 'Badge');
  if (!elHari || !elPasaran || !badge) return;

  const h = elHari.value;
  const p = elPasaran.value;
  const hIdx = HARI.indexOf(h);
  const pIdx = PASARAN.indexOf(p);
  if (hIdx >= 0 && pIdx >= 0) {
    const n = NEPTU_HARI[hIdx] + NEPTU_PASARAN[pIdx];
    badge.innerText = n;
  } else {
    badge.innerText = '-';
  }
}

export async function hitungNujumPerjodohan() {
  try {
    await loadDomainData('jodoh');
  } catch (e) {
    console.warn('[jodoh-ui] hitungNujumPerjodohan fallback:', e);
  }
  const neptuPText = document.getElementById('neptuPBadge')?.innerText;
  const neptuLText = document.getElementById('neptuLBadge')?.innerText;
  const neptuP = parseInt(neptuPText || '');
  const neptuL = parseInt(neptuLText || '');

  if (!neptuP || !neptuL || isNaN(neptuP) || isNaN(neptuL)) {
    showToast('Pilih tanggal lahir atau tentukan weton kedua calon pengantin terlebih dahulu.');
    return;
  }

  const hariP = document.getElementById('hariP')?.value || '';
  const hariL = document.getElementById('hariL')?.value || '';
  const pasaranP = document.getElementById('pasaranP')?.value || '';
  const pasaranL = document.getElementById('pasaranL')?.value || '';
  const namaP = document.getElementById('namaP')?.value.trim() || 'Calon Pengantin Wanita';
  const namaL = document.getElementById('namaL')?.value.trim() || 'Calon Pengantin Pria';
  const tglP = document.getElementById('tglP')?.value || '';
  const tglL = document.getElementById('tglL')?.value || '';

  if (!tglP || !tglL) {
    showToast('Tanggal lahir kedua calon pasangan wajib diisi sebelum melakukan perhitungan.');
    if (!tglP) {
      document.getElementById('tglP')?.focus();
    } else {
      document.getElementById('tglL')?.focus();
    }
    return;
  }

  const akDP = document.getElementById('aksaraDepanP')?.value || 'HA';
  const akBP = document.getElementById('aksaraBelakangP')?.value || 'HA';
  const akDL = document.getElementById('aksaraDepanL')?.value || 'HA';
  const akBL = document.getElementById('aksaraBelakangL')?.value || 'HA';

  let domainData = null;
  try {
    domainData = await loadDomainData('jodoh');
  } catch (e) {
    console.warn('[jodoh-ui] hitungNujumPerjodohan error:', e);
  }

  const hasil = hitungPitung7Metode(
    { nama: namaP, hari: hariP, pasaran: pasaranP, neptu: neptuP, aksaraDepan: akDP, aksaraBelakang: akBP },
    { nama: namaL, hari: hariL, pasaran: pasaranL, neptu: neptuL, aksaraDepan: akDL, aksaraBelakang: akBL },
    domainData || {}
  );

  // Periksa Status Hubungan (planning / engaged / married)
  const statusEl = document.querySelector('input[name="statusHubunganPerjodohan"]:checked') || document.getElementById('statusHubunganPerjodohan');
  const statusHubungan = normalizeStatusHubungan(statusEl ? statusEl.value : 'planning');
  const isMarried = (statusHubungan === 'married');

  // Cari usulan tanggal mantu rahayu (hanya jika planning atau engaged)
  const mantuList = (!isMarried) ? cariRekomendasiTanggalMantu(hasil.totalNeptu, { rentang: currentMantuRentang || '1-tahun', count: 15, hanyaUtama: currentMantuHanyaUtama }) : [];

  // Simpan ke riwayat lokal (Tahap 4.3)
  saveJodohHistory(hasil, { tglP, tglL, statusHubungan });

  // Simpan state untuk ekspor PDF
  window.LAST_PERJODOHAN_DATA = hasil;
  window.LAST_MANTU_DATA = mantuList;
  window.LAST_STATUS_HUBUNGAN = statusHubungan;

  // Render Tabel 7 Metode
  let tbody = '';
  hasil.rows.forEach(r => {
    let badgeClass = '';
    let badgeSymbol = '';
    if (r.h.status === 'baik') {
      badgeClass = 'bg-emerald-950/80 text-emerald-300 border-emerald-500/70';
      badgeSymbol = '✓ BAIK';
    } else if (r.h.status === 'buruk') {
      badgeClass = 'bg-rose-950/80 text-rose-300 border-rose-500/70';
      badgeSymbol = '⊗ BURUK';
    } else {
      badgeClass = 'bg-amber-950/80 text-amber-300 border-amber-500/70';
      badgeSymbol = '• CAMPUR';
    }

    tbody += `
      <tr class="border-b border-sogan-800/80 hover:bg-sogan-900/30">
        <td class="p-3 font-bold text-prada">${r.no}</td>
        <td class="p-3 font-marcellus font-bold text-sogan-100">${r.h.nama}</td>
        <td class="p-3 text-sogan-200">${r.h.arti} <div class="text-[10px] text-sogan-400 font-mono mt-0.5">${r.rumus}</div></td>
        <td class="p-3 text-center"><span class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-[10px] font-bold ${badgeClass}">${badgeSymbol}</span></td>
      </tr>
    `;
  });

  const hasilBody = document.getElementById('hasilPerjodohanBody');
  if (hasilBody) hasilBody.innerHTML = tbody;

  const pairInfo = document.getElementById('perjodohanPairInfo');
  if (pairInfo) {
    const statusLabel = getStatusHubunganLabel(statusHubungan);
    pairInfo.innerHTML = `${namaP} (${neptuP}) &middot; ${namaL} (${neptuL}) &bull; Jumlah Neptu: <strong class="text-prada font-mono">${hasil.totalNeptu}</strong> &bull; Status: <span class="text-amber-300 font-semibold">${statusLabel}</span>`;
  }

  // Render Visual Score Bar & Hero Gauge (Tahap 4.1)
  renderVisualScoreBar(hasil.summary);

  // Render Usulan 5 Tanggal Mantu (Belum Menikah / Engaged) ATAU Panduan Keharmonisan Pasutri (Sudah Menikah)
  if (isMarried) {
    renderPanduanKeharmonisanPasutri(hasil);
  } else {
    renderRekomendasiMantu(mantuList);
  }

  // Render Riwayat Hitungan Pasangan (Tahap 4.3)
  renderRiwayatPerjodohan();

  // Render Disclaimer Etis Perjodohan (Tahap 4.4 / 3.5)
  const disclaimerBox = document.getElementById('disclaimerPerjodohanBox');
  if (disclaimerBox) {
    disclaimerBox.innerHTML = `
      <div class="p-3.5 rounded-xl bg-sogan-950/90 border border-sogan-800 text-[11px] text-sogan-300 flex items-start gap-2.5">
        <i class="fa-solid fa-scroll text-prada text-base mt-0.5"></i>
        <div class="space-y-1">
          <strong class="text-prada block font-semibold uppercase text-[10px] tracking-wider">Amanat Budaya &amp; Literasi Mawas Diri:</strong>
          <p class="leading-relaxed italic">${DISCLAIMER_ETIS_PERJODOHAN}</p>
        </div>
      </div>
    `;
  }

  const emptyBox = document.getElementById('emptyPerjodohanBox');
  if (emptyBox) emptyBox.classList.add('hidden');
  const cardBox = document.getElementById('hasilPerjodohanCard');
  if (cardBox) cardBox.classList.remove('hidden');

  const btnParchment = document.getElementById('btnPrintPerjodohanParchment');
  if (btnParchment) btnParchment.style.display = 'inline-flex';
  const btnLegacy = document.getElementById('btnPrintPerjodohan');
  if (btnLegacy) btnLegacy.style.display = 'inline-flex';

  // Bawa stepper ke Langkah 4
  applyJodohWizardStepUI(4);

  showToast('Pitung perjodohan kasil kapetung kanthi jangkep!');
}

/**
 * Render visualisasi skor proporsi (Baik - Campur - Buruk) dan hero badge (Tahap 4.1).
 * @param {Object} summary 
 */
export function renderVisualScoreBar(summary) {
  const container = document.getElementById('visualScorePerjodohanBox');
  if (!container) return;

  const baikPct = Math.round((summary.baik / summary.total) * 100);
  const campurPct = Math.round((summary.campur / summary.total) * 100);
  const burukPct = Math.round((summary.buruk / summary.total) * 100);
  const keharmonisan = summary.keharmonisan || getTingkatKeharmonisan(summary.skorKeselarasan);

  container.innerHTML = `
    <div class="p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-sogan-950 via-keraton to-wulung border border-prada/40 space-y-4 shadow-lg">
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-sogan-800 pb-3">
        <div>
          <span class="text-[10px] font-mono text-prada uppercase tracking-widest font-semibold block">HASIL EVALUASI TINGKAT KEHARMONISAN</span>
          <h3 class="font-marcellus text-lg sm:text-xl font-bold text-prada-light">${keharmonisan.predikat}</h3>
          <p class="text-xs text-sogan-300 mt-0.5">${keharmonisan.deskripsi}</p>
        </div>
        <div class="text-left sm:text-right">
          <div class="inline-flex items-baseline gap-1.5 px-4 py-2 rounded-2xl bg-sogan-900/90 border border-prada/60 shadow">
            <span class="text-2xl sm:text-3xl font-bold font-mono gold-gradient-text">${summary.skorKeselarasan}%</span>
            <span class="text-[10px] text-sogan-400 uppercase font-mono">Keselarasan</span>
          </div>
        </div>
      </div>

      <!-- Segmented Proportion Visual Bar (4.1) -->
      <div class="space-y-1.5">
        <div class="flex flex-wrap justify-between text-[11px] font-mono gap-1">
          <span class="text-emerald-400 font-semibold"><i class="fa-solid fa-circle-check mr-1"></i>${summary.baik} Metode Baik (${baikPct}%)</span>
          <span class="text-amber-400 font-semibold"><i class="fa-solid fa-circle-minus mr-1"></i>${summary.campur} Metode Netral (${campurPct}%)</span>
          <span class="text-rose-400 font-semibold"><i class="fa-solid fa-circle-xmark mr-1"></i>${summary.buruk} Metode Perhatian (${burukPct}%)</span>
        </div>
        <div class="w-full h-3.5 rounded-full overflow-hidden flex bg-sogan-950 border border-sogan-800 p-0.5 shadow-inner">
          ${summary.baik > 0 ? `<div style="width: ${baikPct}%" class="bg-gradient-to-r from-emerald-600 to-teal-500 rounded-l-full transition-all duration-500" title="Metode Baik: ${summary.baik}"></div>` : ''}
          ${summary.campur > 0 ? `<div style="width: ${campurPct}%" class="bg-gradient-to-r from-amber-500 to-yellow-400 transition-all duration-500" title="Metode Campur: ${summary.campur}"></div>` : ''}
          ${summary.buruk > 0 ? `<div style="width: ${burukPct}%" class="bg-gradient-to-r from-rose-600 to-red-500 rounded-r-full transition-all duration-500" title="Metode Perhatian: ${summary.buruk}"></div>` : ''}
        </div>
      </div>

      <!-- Nasihat Kultural & Tepa Slira -->
      <div class="p-3 rounded-xl bg-keraton border border-sogan-800/90 text-xs flex items-start gap-2.5">
        <i class="fa-solid fa-feather text-prada text-sm mt-0.5"></i>
        <div class="space-y-0.5">
          <strong class="text-prada block text-[11px] uppercase tracking-wider">Pandom Batin &amp; Tepa Slira:</strong>
          <span class="text-sogan-200 leading-relaxed italic text-[11.5px]">${keharmonisan.saranKultural}</span>
        </div>
      </div>
    </div>
  `;
}

/**
 * Render panduan keharmonisan dan evaluasi khusus pasangan yang sudah menikah (Pasutri).
 * @param {Object} hasil 
 */
export function renderPanduanKeharmonisanPasutri(hasil) {
  const container = document.getElementById('rekomendasiMantuBox');
  if (!container) return;

  const keharmonisan = getTingkatKeharmonisan(hasil?.summary || { baik: 0, buruk: 0, netral: 0 });
  const totalNeptu = hasil?.totalNeptu || 0;

  container.innerHTML = `
    <div class="p-4 sm:p-6 rounded-2xl bg-wulung border border-emerald-500/40 space-y-4 shadow-xl">
      <div class="flex flex-wrap items-center justify-between gap-2 border-b border-sogan-800 pb-3">
        <div>
          <div class="flex items-center gap-2">
            <span class="text-[10px] font-mono text-emerald-300 uppercase tracking-widest font-bold block px-2.5 py-0.5 rounded-full bg-emerald-950/80 border border-emerald-600/60">
              <i class="fa-solid fa-house-chimney-window mr-1"></i> STATUS: SAMPUN NIKAH (PASUTRI)
            </span>
          </div>
          <h4 class="font-marcellus text-lg sm:text-xl font-bold text-prada-light mt-1.5">
            Pituduh Gesang Bebrayan &amp; Pangruwating Pasulayan
          </h4>
          <p class="text-[11px] text-sogan-300">
            Panduan kearifan lokal kanggé ngreksa karukunan bale wisma, nyelarasaken watak, sarta ngruwat rubeda.
          </p>
        </div>
        <span class="text-[11px] px-3.5 py-1.5 rounded-full bg-sogan-950 border border-prada/50 text-prada font-mono">
          Neptu Jangkep: ${totalNeptu}
        </span>
      </div>

      <!-- Ringkasan Tingkat Keharmonisan -->
      <div class="p-4 rounded-xl bg-keraton border border-sogan-800 space-y-2">
        <div class="flex items-center justify-between">
          <span class="text-xs text-sogan-400 font-mono uppercase font-semibold">Tingkat Karukunan:</span>
          <span class="px-2.5 py-0.5 rounded-full font-bold text-xs ${keharmonisan.badgeClass}">
            ${keharmonisan.tingkat}
          </span>
        </div>
        <p class="text-xs text-sogan-200 leading-relaxed">${keharmonisan.deskripsi}</p>
      </div>

      <!-- Grid 3 Pilar Keharmonisan Rumah Tangga -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
        <!-- 1. Mitigasi Pasulayan -->
        <div class="p-3.5 rounded-xl bg-keraton border border-sogan-800 space-y-2">
          <span class="text-[10px] uppercase font-bold text-amber-300 flex items-center gap-1.5 tracking-wider">
            <i class="fa-solid fa-shield-heart text-amber-400"></i> Pangruwating Rubeda
          </span>
          <p class="text-sogan-300 text-[11px] leading-relaxed">
            Menawi wonten asil petung ingkang kirang sae, punika boten ateges pepisahan, ananging pepeling supados tansah sabar, ngalah, lan nyuda hawa nepsu.
          </p>
        </div>

        <!-- 2. Laku Pangayoman -->
        <div class="p-3.5 rounded-xl bg-keraton border border-sogan-800 space-y-2">
          <span class="text-[10px] uppercase font-bold text-emerald-300 flex items-center gap-1.5 tracking-wider">
            <i class="fa-solid fa-seedling text-emerald-400"></i> Laku Bebrayan
          </span>
          <p class="text-sogan-300 text-[11px] leading-relaxed">
            Nindakaken sedekah weton kanthi tulusing manah, ngraketaken silaturahmi kaliyan tiyang sepuh kakalih, sarta dedonga nyenyuwun sih wilasaning Gusti.
          </p>
        </div>

        <!-- 3. Visi Langgeng -->
        <div class="p-3.5 rounded-xl bg-keraton border border-sogan-800 space-y-2">
          <span class="text-[10px] uppercase font-bold text-teal-300 flex items-center gap-1.5 tracking-wider">
            <i class="fa-solid fa-infinity text-teal-400"></i> Hayuning Bawana
          </span>
          <p class="text-sogan-300 text-[11px] leading-relaxed">
            Urip bebrayan punika sarana ngasuh katresnan sejati, dados tuladha kautaman tumrap para putra, dumugi kaken-kaken ninen-ninen ing karaharjan.
          </p>
        </div>
      </div>

      <!-- Nasihat Kultural Khusus -->
      <div class="p-3.5 rounded-xl bg-sogan-950/80 border border-prada/30 text-xs text-sogan-300 flex items-start gap-3">
        <i class="fa-solid fa-quote-left text-prada text-base mt-1 flex-shrink-0"></i>
        <div class="space-y-1">
          <strong class="text-prada-light block text-[11px]">Paweling Kagem Pasutri:</strong>
          <p class="text-[11px] leading-relaxed italic text-sogan-200">
            "${keharmonisan.saranKultural}"
          </p>
        </div>
      </div>
    </div>
  `;
}

let currentMantuRentang = '1-tahun'; // '1-tahun' | 'tahun-depan'
let currentMantuHanyaUtama = false;

export function setMantuRentang(rentang) {
  currentMantuRentang = rentang;
  const totalNeptu = window.LAST_PERJODOHAN_DATA?.totalNeptu;
  if (!totalNeptu) return;
  const list = cariRekomendasiTanggalMantu(totalNeptu, {
    rentang,
    count: 15,
    hanyaUtama: currentMantuHanyaUtama
  });
  window.LAST_MANTU_DATA = list;
  renderRekomendasiMantu(list);
}

export function toggleMantuHanyaUtama() {
  currentMantuHanyaUtama = !currentMantuHanyaUtama;
  const totalNeptu = window.LAST_PERJODOHAN_DATA?.totalNeptu;
  if (!totalNeptu) return;
  const list = cariRekomendasiTanggalMantu(totalNeptu, {
    rentang: currentMantuRentang,
    count: 15,
    hanyaUtama: currentMantuHanyaUtama
  });
  window.LAST_MANTU_DATA = list;
  renderRekomendasiMantu(list);
}

/**
 * Render rekomendasi tanggal mantu rahayu (Tahap 4.2).
 * Menampilkan pilihan alternatif 1 tahun penuh atau tahun depan,
 * lengkap dengan penanda hari paling utama (Sri).
 * @param {Array<Object>} mantuList 
 */
export function renderRekomendasiMantu(mantuList) {
  const container = document.getElementById('rekomendasiMantuBox');
  if (!container) return;

  if (!mantuList || mantuList.length === 0) {
    container.innerHTML = `
      <div class="p-4 rounded-xl bg-keraton border border-sogan-800 text-center text-xs text-sogan-400">
        Pencarian tanggal rahayu memerlukan perhitungan neptu pasangan terlebih dahulu.
      </div>
    `;
    return;
  }

  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;
  const isTahunDepan = (currentMantuRentang === 'tahun-depan');

  container.innerHTML = `
    <div class="p-4 sm:p-5 rounded-2xl bg-wulung border border-prada/40 space-y-4">
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-sogan-800 pb-3">
        <div>
          <span class="text-[10px] font-mono text-prada uppercase tracking-widest font-semibold block">REKOMENDASI PAWIWAHAN (4.2)</span>
          <h4 class="font-marcellus text-lg font-bold text-prada-light">5 Usulan Tanggal Mantu Rahayu &amp; Alternatif Pilihan</h4>
          <p class="text-[11px] text-sogan-300">Dipilih miturut kombinasi <strong>Dino Ijo</strong> (hari baik), non-Dino Gede, sarta predikat <strong>Panca Sudha Ijab Mantu</strong> (Sri / Lungguh / Gedhong).</p>
        </div>
        
        <!-- Controls: Pilihan Rentang (1 Tahun vs Tahun Berikutnya) & Filter Hari Utama -->
        <div class="flex flex-wrap items-center gap-1.5 shrink-0">
          <div class="p-1 rounded-xl bg-sogan-950 border border-sogan-800 flex items-center gap-1">
            <button
              type="button"
              onclick="setMantuRentang('1-tahun')"
              class="px-2.5 py-1 rounded-lg text-xs font-semibold transition cursor-pointer ${!isTahunDepan ? 'bg-prada text-keraton font-bold shadow' : 'text-sogan-300 hover:text-amber-100'}"
            >
              1 Tahun Penuh
            </button>
            <button
              type="button"
              onclick="setMantuRentang('tahun-depan')"
              class="px-2.5 py-1 rounded-lg text-xs font-semibold transition cursor-pointer ${isTahunDepan ? 'bg-prada text-keraton font-bold shadow' : 'text-sogan-300 hover:text-amber-100'}"
            >
              Tahun ${nextYear}
            </button>
          </div>

          <button
            type="button"
            onclick="toggleMantuHanyaUtama()"
            class="px-2.5 py-1.5 rounded-xl border text-xs font-semibold transition cursor-pointer flex items-center gap-1 ${currentMantuHanyaUtama ? 'bg-amber-500/20 text-amber-200 border-amber-400' : 'bg-sogan-950 text-sogan-300 border-sogan-800 hover:text-prada'}"
            title="Saring hanya hari dengan predikat Paling Utama (Sri)"
          >
            <i class="fa-solid fa-star text-amber-400 text-[10px]"></i>
            <span>Hanya Paling Utama (Sri)</span>
          </button>
        </div>
      </div>

      <div class="flex items-center justify-between text-xs text-sogan-400">
        <span>Menampilkan <strong>${mantuList.length}</strong> tanggal alternatif pilihan (${isTahunDepan ? `Tahun ${nextYear}` : '1 Tahun Kedepan'}):</span>
        <span class="text-[11px] text-amber-300 font-mono flex items-center gap-1">
          <i class="fa-solid fa-star text-amber-400"></i> Hari Paling Utama ditandai warna emas
        </span>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-xs max-h-[600px] overflow-y-auto pr-1 custom-scrollbar">
        ${mantuList.map((m, idx) => {
          const isTopPrime = m.isPalingUtama;
          const cardBorder = isTopPrime
            ? 'border-amber-400/80 bg-gradient-to-b from-[#1c160c] via-keraton to-keraton shadow-[0_0_15px_rgba(234,179,8,0.15)] ring-1 ring-amber-400/30'
            : 'border-sogan-800 hover:border-prada/60 bg-keraton shadow';

          return `
          <div class="p-3.5 rounded-xl border ${cardBorder} transition space-y-2.5 flex flex-col justify-between">
            <div class="space-y-1">
              <div class="flex items-center justify-between gap-1">
                <div class="flex items-center gap-1.5">
                  <span class="text-[10px] font-mono px-2 py-0.5 rounded-full ${isTopPrime ? 'bg-amber-400 text-keraton font-bold' : 'bg-sogan-900 border border-sogan-700 text-amber-300 font-bold'}">
                    #${idx + 1}
                  </span>
                  ${isTopPrime ? `
                    <span class="text-[10px] px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-500/25 to-yellow-500/30 text-amber-200 border border-amber-400 font-bold flex items-center gap-1">
                      <i class="fa-solid fa-star text-amber-400 text-[9px]"></i> Paling Utama (Sri)
                    </span>
                  ` : (m.sisaPancaSudha === 2 ? `
                    <span class="text-[10px] px-2 py-0.5 rounded-full bg-emerald-950/80 text-emerald-300 border border-emerald-600/60 font-semibold flex items-center gap-1">
                      <i class="fa-solid fa-crown text-[9px]"></i> Utama (Lungguh)
                    </span>
                  ` : `
                    <span class="text-[10px] px-2 py-0.5 rounded-full bg-cyan-950/80 text-cyan-300 border border-cyan-600/60 font-medium flex items-center gap-1">
                      <i class="fa-solid fa-gem text-[9px]"></i> Sae (Gedhong)
                    </span>
                  `)}
                </div>
                ${m.isAkhirPekan ? `
                  <span class="text-[9px] px-1.5 py-0.5 rounded bg-sogan-900 border border-sogan-700 text-amber-200/90 font-mono">
                    Akhir Pekan
                  </span>
                ` : ''}
              </div>
              
              <h5 class="font-marcellus text-sm font-bold ${isTopPrime ? 'text-amber-100' : 'text-amber-200'} pt-0.5">
                ${m.formattedDate}
              </h5>
              
              <div class="text-[11px] text-amber-100/90 font-medium">
                Weton: <strong class="text-prada-light font-bold">${m.weton}</strong> (Neptu: ${m.neptuHari}) &middot; Wuku ${m.wukuName}
              </div>
            </div>

            <div class="p-2.5 rounded-lg bg-[#140e08]/95 border ${isTopPrime ? 'border-amber-700/60' : 'border-amber-900/60'} space-y-1.5 text-[11px]">
              <div class="flex items-center justify-between border-b border-amber-950/60 pb-1">
                <span class="text-amber-200/80 font-medium">Panca Sudha:</span>
                <strong class="${isTopPrime ? 'text-amber-300 font-bold' : 'text-amber-300 font-semibold'}">${m.kategoriLabel}</strong>
              </div>
              <p class="text-amber-100 text-[11px] italic leading-relaxed">${m.makna}</p>
            </div>

            <div class="grid grid-cols-2 gap-1.5 pt-1">
              <button onclick="simpanMantuKeBookmark('${m.dateStr}', '${m.weton}', '${m.predikat}')" class="min-h-[44px] py-1.5 px-2 rounded-lg bg-sogan-900 hover:bg-sogan-800 border border-prada/40 hover:border-prada text-prada text-[11px] font-semibold flex items-center justify-center gap-1 transition cursor-pointer">
                <i class="fa-solid fa-bookmark text-[10px]"></i> Tandai
              </button>
              <button onclick="navigateToIjabDariMantu('${m.dateStr}')" class="min-h-[44px] py-1.5 px-2 rounded-lg bg-teal-950/80 hover:bg-teal-900 border border-teal-600/60 text-teal-300 text-[11px] font-semibold flex items-center justify-center gap-1 transition cursor-pointer" title="Periksa keselarasan lengkap di modul Petung Ijab">
                <i class="fa-solid fa-arrow-up-right-from-square text-[10px]"></i> Buka Ijab
              </button>
            </div>
          </div>
          `;
        }).join('')}
      </div>

      <!-- Banner CTA Navigasi ke Petung Ijab -->
      <div class="p-3.5 rounded-xl bg-teal-950/60 border border-teal-700/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
        <div class="flex items-start gap-2.5">
          <i class="fa-solid fa-ring text-teal-400 text-base mt-0.5"></i>
          <div>
            <strong class="text-teal-200 block font-semibold text-xs">Butuh Analisis Hari Akad / Ijab Komprehensif?</strong>
            <p class="text-[11px] text-teal-100/80 leading-relaxed">Gunakan modul Petung Ijab Nikah untuk memeriksa kecocokan mendalam terhadap wuku penganten dan pantangan adat sakral.</p>
          </div>
        </div>
        <button onclick="navigateToIjabDariMantu()" class="min-h-[44px] px-4 py-2 rounded-xl bg-teal-800 hover:bg-teal-700 text-white font-bold text-xs flex items-center justify-center gap-2 cursor-pointer transition flex-shrink-0 shadow">
          <span data-i18n="jodoh_cta_buka_ijab">Buka Rekomendasi Hari Baik di Petung Ijab</span>
          <i class="fa-solid fa-arrow-right text-[10px]"></i>
        </button>
      </div>
    </div>
  `;
}

/**
 * Menyimpan tanggal mantu langsung ke LocalStorage Bookmark Kalender.
 * @param {string} dateStr 
 * @param {string} weton 
 * @param {string} predikat 
 */
export function simpanMantuKeBookmark(dateStr, weton, predikat) {
  saveBookmark(dateStr, {
    catatan: `Rencana Ijab Mantu (${predikat}) &middot; Weton: ${weton}`,
    kategori: 'pawiwahan'
  });
  showToast(`Tanggal mantu ${weton} kasil katandha ing kalender!`);
}

/**
 * Render riwayat perhitungan pasangan (Tahap 4.3).
 */
export function renderRiwayatPerjodohan() {
  const container = document.getElementById('riwayatPerjodohanList');
  if (!container) return;

  const history = getJodohHistory();
  if (history.length === 0) {
    container.innerHTML = `
      <div class="text-center py-6 text-sogan-400 text-xs">
        <i class="fa-solid fa-clock-rotate-left text-2xl text-sogan-600 mb-2"></i>
        <p>Dereng wonten riwayat petungan pasangan ingkang kasimpen.</p>
      </div>
    `;
    return;
  }

  container.innerHTML = `
    <div class="space-y-2 text-xs">
      ${history.map(item => `
        <div class="p-3 rounded-xl bg-keraton border border-sogan-800 flex flex-wrap items-center justify-between gap-2.5 hover:border-prada/50 transition">
          <div class="space-y-0.5">
            <div class="flex items-center gap-2">
              <strong class="font-marcellus text-sm text-prada">${item.wanita.nama} &amp; ${item.pria.nama}</strong>
              <span class="text-[10px] font-mono px-2 py-0.5 rounded-full bg-sogan-900 text-amber-300 border border-sogan-700">
                Neptu: ${item.totalNeptu}
              </span>
              ${item.statusHubungan === 'sudah_menikah'
                ? '<span class="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-950/80 border border-emerald-600/60 text-emerald-300 font-semibold">Pasutri</span>'
                : '<span class="text-[9px] font-mono px-1.5 py-0.5 rounded bg-amber-950/80 border border-amber-600/60 text-amber-300 font-semibold">Calon Penganten</span>'}
            </div>
            <div class="text-[11px] text-sogan-400">
              ${item.wanita.hari} ${item.wanita.pasaran} (${item.wanita.neptu}) &middot; ${item.pria.hari} ${item.pria.pasaran} (${item.pria.neptu}) &mdash; Keselarasan: <strong class="text-emerald-400">${item.skorKeselarasan}%</strong> (${item.predikatKeharmonisan})
            </div>
            <span class="text-[10px] font-mono text-sogan-500 block">${item.tglHitungStr}</span>
          </div>
          <div class="flex items-center gap-2">
            <button onclick="loadRiwayatPerjodohan('${item.id}')" class="px-3 py-1.5 rounded-lg bg-sogan-900 border border-prada/50 text-prada hover:bg-prada/20 font-semibold text-xs flex items-center gap-1 transition cursor-pointer">
              <i class="fa-solid fa-arrow-rotate-left"></i> Muat
            </button>
            <button onclick="hapusRiwayatPerjodohan('${item.id}')" class="w-8 h-8 rounded-lg bg-rose-950/60 border border-rose-800 text-rose-300 hover:bg-rose-900 flex items-center justify-center transition cursor-pointer" title="Hapus Riwayat">
              <i class="fa-solid fa-trash text-xs"></i>
            </button>
          </div>
        </div>
      `).join('')}
    </div>
  `;
}

/**
 * Memuat kembali riwayat pasangan ke form input dan langsung menghitung ulang.
 * @param {string} id 
 */
export function loadRiwayatPerjodohan(id) {
  const history = getJodohHistory();
  const item = history.find(x => x.id === id);
  if (!item) return;

  const elNamaP = document.getElementById('namaP');
  const elNamaL = document.getElementById('namaL');
  const elTglP = document.getElementById('tglP');
  const elTglL = document.getElementById('tglL');
  const elHariP = document.getElementById('hariP');
  const elHariL = document.getElementById('hariL');
  const elPasaranP = document.getElementById('pasaranP');
  const elPasaranL = document.getElementById('pasaranL');

  if (elNamaP) elNamaP.value = item.wanita.nama || '';
  if (elNamaL) elNamaL.value = item.pria.nama || '';
  if (elTglP && item.wanita.tglLahir) elTglP.value = item.wanita.tglLahir;
  if (elTglL && item.pria.tglLahir) elTglL.value = item.pria.tglLahir;

  // Pulihkan status hubungan radio button (planning / engaged / married)
  if (item.statusHubungan) {
    const norm = normalizeStatusHubungan(item.statusHubungan);
    const radioPlanning = document.getElementById('statusBelumMenikah');
    const radioEngaged = document.getElementById('statusEngaged');
    const radioMarried = document.getElementById('statusSudahMenikah');

    if (norm === 'married') {
      if (radioMarried) radioMarried.checked = true;
    } else if (norm === 'engaged') {
      if (radioEngaged) radioEngaged.checked = true;
    } else {
      if (radioPlanning) radioPlanning.checked = true;
    }
    window.LAST_STATUS_HUBUNGAN = norm;
  }

  autoDetectAksaraUI('P');
  autoDetectAksaraUI('L');

  if (elHariP && item.wanita.hari) elHariP.value = item.wanita.hari;
  if (elPasaranP && item.wanita.pasaran) elPasaranP.value = item.wanita.pasaran;
  if (elHariL && item.pria.hari) elHariL.value = item.pria.hari;
  if (elPasaranL && item.pria.pasaran) elPasaranL.value = item.pria.pasaran;

  updateNeptuPerjodohan('P');
  updateNeptuPerjodohan('L');

  hitungNujumPerjodohan();
  showToast('Riwayat pitung kasil dipun muat!');
}

/**
 * Menghapus satu entri riwayat hitungan pasangan.
 * @param {string} id 
 */
export function hapusRiwayatPerjodohan(id) {
  deleteJodohHistoryItem(id);
  renderRiwayatPerjodohan();
  showToast('Entri riwayat pitung kasil dibusak.');
}

/**
 * Membersihkan seluruh riwayat hitungan pasangan.
 */
export function clearAllRiwayatPerjodohan() {
  clearAllJodohHistory();
  renderRiwayatPerjodohan();
  showToast('Sedaya riwayat pitung kasil dibusak.');
}

/**
 * Cetak Laporan Perjodohan Kasultanan Resmi (Tahap 4.4).
 * @param {'parchment'|'monochrome'} theme 
 */
export function printLaporanPerjodohan(theme = 'parchment') {
  if (!window.LAST_PERJODOHAN_DATA) {
    if (typeof hitungNujumPerjodohan === 'function') {
      hitungNujumPerjodohan();
    }
  }
  if (!window.LAST_PERJODOHAN_DATA) {
    showToast('Hitung pitung perjodohan terlebih dahulu sebelum mencetak.');
    return;
  }

  const hasil = window.LAST_PERJODOHAN_DATA;
  const isSudahMenikah = (window.LAST_STATUS_HUBUNGAN === 'sudah_menikah');
  const mantuList = isSudahMenikah ? [] : (window.LAST_MANTU_DATA || cariRekomendasiTanggalMantu(hasil.totalNeptu));
  const keharmonisan = hasil.summary.keharmonisan || getTingkatKeharmonisan(hasil.summary.skorKeselarasan);

  let container = document.getElementById('laporan-cetak-pdf');
  if (!container) {
    container = document.createElement('div');
    container.id = 'laporan-cetak-pdf';
    container.className = 'print-only-document';
    document.body.appendChild(container);
  } else if (container.parentElement !== document.body) {
    document.body.appendChild(container);
  }

  const isParchment = theme === 'parchment';
  container.className = `print-only-document ${isParchment ? 'theme-parchment parchment-theme' : 'theme-monochrome monochrome-theme'}`;

  // Styling palette untuk cetak (light parchment atau monochrome)
  const boxBg = isParchment ? '#fbf5e6' : '#ffffff';
  const boxBorder = isParchment ? '#c8a355' : '#374151';
  const textDark = isParchment ? '#2b1d0c' : '#111827';
  const textMuted = isParchment ? '#634720' : '#4b5563';
  const goldHeading = isParchment ? '#8c6224' : '#111827';

  container.innerHTML = `
    <div class="print-report-wrapper" style="width: 100%; box-sizing: border-box;">
      <div class="laporan-page single-page-sheet ${isParchment ? 'theme-parchment' : 'theme-monochrome'}" style="max-width: 860px; margin: 0 auto; padding: 18px 24px; ${isParchment ? 'background-color: #fdf6e2; color: #2b1d0c; border: 3px double #8c6224; outline: 1.5px solid #d4af37; outline-offset: -5px;' : 'background-color: #ffffff; color: #111827; border: 2px solid #374151;'} border-radius: 4px; position: relative; font-family: 'Times New Roman', Georgia, serif; box-sizing: border-box;">
        
        <span class="corner-tr" aria-hidden="true" style="position: absolute; top: 4px; right: 5px; color: ${goldHeading}; font-size: 14pt;">❖</span>
        <span class="corner-bl" aria-hidden="true" style="position: absolute; bottom: 4px; left: 5px; color: ${goldHeading}; font-size: 14pt;">❖</span>

        <!-- Kop Surat Kasultanan Resmi -->
        <div class="kop-surat flex items-center justify-between border-b-2 border-[#8c6224] pb-2 mb-2">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full border-2 border-[#8c6224] flex items-center justify-center font-cinzel text-lg font-bold text-[#8c6224]">
              ꦗ
            </div>
            <div>
              <h2 class="font-cinzel text-base font-bold tracking-wider text-[#8c6224]">JAGAD JAWA &middot; SERAT PITUNG SALAKI RABI</h2>
              <p class="text-[9.5px] tracking-wide text-[#634720]">Pawiyatan Luhur Budaya Nusantara &bull; Surat Katrangan Petungan Perjodohan Jawa</p>
            </div>
          </div>
          <div class="text-right text-[9.5px] font-mono text-[#634720]">
            <div>Tanggal Cetak: ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
            <div>Status: <strong>${isSudahMenikah ? 'Pasutri (Sampun Nikah)' : 'Calon Penganten'}</strong></div>
          </div>
        </div>

        <!-- Profil Kedua Calon Mempelai / Pasutri -->
        <div class="grid grid-cols-2 gap-2.5 text-xs mb-2">
          <div class="p-2.5 rounded border" style="background-color: ${boxBg}; border-color: ${boxBorder}; color: ${textDark};">
            <span class="text-[9.5px] font-mono uppercase font-bold block text-[#8c6224]">${isSudahMenikah ? 'Garwa Wanita (♀)' : 'Calon Pengantin Wanita (♀)'}</span>
            <div class="font-bold text-sm text-[#2b1d0c]">${hasil.wanita.nama}</div>
            <div class="text-[10.5px] text-[#422c10]">Weton: <strong>${hasil.wanita.hari} ${hasil.wanita.pasaran}</strong> (Neptu: ${hasil.wanita.neptu})</div>
            <div class="text-[9.5px] text-[#634720]">Aksara: ${hasil.wanita.aksaraDepan} / ${hasil.wanita.aksaraBelakang}</div>
          </div>

          <div class="p-2.5 rounded border" style="background-color: ${boxBg}; border-color: ${boxBorder}; color: ${textDark};">
            <span class="text-[9.5px] font-mono uppercase font-bold block text-[#8c6224]">${isSudahMenikah ? 'Garwa Kakung (♂)' : 'Calon Pengantin Pria (♂)'}</span>
            <div class="font-bold text-sm text-[#2b1d0c]">${hasil.pria.nama}</div>
            <div class="text-[10.5px] text-[#422c10]">Weton: <strong>${hasil.pria.hari} ${hasil.pria.pasaran}</strong> (Neptu: ${hasil.pria.neptu})</div>
            <div class="text-[9.5px] text-[#634720]">Aksara: ${hasil.pria.aksaraDepan} / ${hasil.pria.aksaraBelakang}</div>
          </div>
        </div>

        <!-- Ringkasan Skor & Predikat Keharmonisan -->
        <div class="p-2.5 rounded border flex items-center justify-between text-xs mb-2" style="background-color: ${boxBg}; border-color: ${boxBorder};">
          <div>
            <span class="text-[9.5px] font-mono uppercase font-bold block text-[#8c6224]">Hasil Evaluasi Keselarasan Pitung</span>
            <div class="font-marcellus text-sm font-bold text-[#2b1d0c]">${keharmonisan.predikat}</div>
            <div class="text-[10.5px] text-[#634720]">Total Neptu Gabungan: <strong class="font-mono text-[#8c6224]">${hasil.totalNeptu}</strong> &bull; Proporsi: ${hasil.summary.baik} Baik, ${hasil.summary.campur} Netral, ${hasil.summary.buruk} Ujian</div>
          </div>
          <div class="text-right">
            <span class="text-xl font-bold font-mono text-[#8c6224]">${hasil.summary.skorKeselarasan}%</span>
          </div>
        </div>

        <!-- Tabel 7 Metode Pitung Jawa -->
        <div class="mb-2">
          <span class="text-[9.5px] font-mono uppercase font-bold tracking-wider block mb-1 text-[#8c6224]">Rincian Petungan 7 Metode Primbon Jawa</span>
          <table class="w-full text-xs border text-left" style="border-color: ${boxBorder};">
            <thead>
              <tr class="border-b text-[9.5px] uppercase font-mono" style="background-color: ${isParchment ? '#efe1be' : '#e5e7eb'}; border-color: ${boxBorder}; color: ${goldHeading};">
                <th class="p-1.5 w-7 text-center">No</th>
                <th class="p-1.5 w-36">Metode &amp; Nama Petung</th>
                <th class="p-1.5">Arti &amp; Wedharan Falsafah</th>
                <th class="p-1.5 w-20 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              ${hasil.rows.map(r => `
                <tr class="border-b text-[10.5px]" style="border-color: ${isParchment ? '#ebdcb6' : '#f3f4f6'};">
                  <td class="p-1.5 font-bold text-center text-[#8c6224]">${r.no}</td>
                  <td class="p-1.5 font-bold text-[#2b1d0c]">${r.h.nama} <div class="text-[8.5px] text-[#634720] font-mono">${r.namaMetode}</div></td>
                  <td class="p-1.5 text-[#2b1d0c]">${r.h.arti} <div class="text-[8.5px] text-[#634720] font-mono">${r.rumus}</div></td>
                  <td class="p-1.5 text-center font-bold text-[9.5px] uppercase ${r.h.status === 'baik' ? 'text-emerald-700' : (r.h.status === 'buruk' ? 'text-rose-700' : 'text-amber-700')}">${r.h.status}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        ${isSudahMenikah ? `
        <!-- Panduan Gesang Bebrayan Pasutri (Sampun Nikah) -->
        <div class="p-2.5 rounded border text-[10.5px] space-y-1 mb-2" style="background-color: ${boxBg}; border-color: ${boxBorder};">
          <span class="text-[9.5px] font-mono uppercase font-bold tracking-wider block text-[#8c6224]">Pituduh Gesang Bebrayan &amp; Pangruwating Pasulayan (Pasutri)</span>
          <p class="text-[#2b1d0c] leading-relaxed italic">"${keharmonisan.saranKultural}"</p>
          <p class="text-[9.5px] text-[#634720]">Urip bebrayan punika sarana ngasuh katresnan sejati, dados tuladha kautaman tumrap para putra, dumugi kaken-kaken ninen-ninen ing karaharjan.</p>
        </div>
        ` : `
        <!-- Usulan 5 Tanggal Mantu Rahayu (Hanya Belum Menikah) -->
        <div class="mb-2">
          <span class="text-[9.5px] font-mono uppercase font-bold tracking-wider block mb-1 text-[#8c6224]">5 Usulan Tanggal Mantu Rahayu Terdekat (Dino Ijo &amp; Panca Sudha)</span>
          <div class="grid grid-cols-5 gap-1.5 text-[9.5px]">
            ${mantuList.slice(0, 5).map(m => `
              <div class="p-1.5 rounded border text-center space-y-0.5" style="background-color: ${boxBg}; border-color: ${boxBorder};">
                <strong class="text-[#8c6224] block text-[9.5px]">${m.formattedDate.split(',')[1]?.trim() || m.formattedDate}</strong>
                <span class="text-[#2b1d0c] block font-semibold text-[9px]">${m.weton}</span>
                <span class="text-[8px] px-1 py-0.2 rounded font-mono inline-block text-amber-800 bg-[#efe1be] border border-[#d4af37]">${m.predikat}</span>
              </div>
            `).join('')}
          </div>
        </div>
        `}

        <!-- Disclaimer Etis & Kultural Perjodohan -->
        <div class="p-2 rounded border text-[9.5px] space-y-0.5 mb-2" style="background-color: ${boxBg}; border-color: ${boxBorder}; color: ${textMuted};">
          <strong class="text-[#8c6224] block uppercase font-mono tracking-wider text-[9px]">Amanat Kultural &amp; Mawas Diri:</strong>
          <p class="leading-relaxed text-[#422c10]">${DISCLAIMER_ETIS_PERJODOHAN}</p>
        </div>

        <!-- Tanda Tangan & Cap Pawiyatan -->
        <div class="flex justify-between items-end pt-1.5 text-[10px] border-t border-[#8c6224]/60">
          <div>
            <span class="text-[#634720] text-[9.5px]">Mugi Hyang Widhi tansah maringi berkah tentrem ing bebrayan.</span>
          </div>
          <div class="text-center">
            <div class="font-marcellus text-[#8c6224] font-bold text-[11px]">JAGAD JAWA NUSANTARA</div>
            <div class="h-6 flex items-center justify-center italic text-[#634720] text-[9px]">[ Cap Pawiyatan Resmi ]</div>
            <div class="text-[9px] text-[#634720] border-t border-[#c8a355] pt-0.5">Pawukon &amp; Primbon Adipati</div>
          </div>
        </div>
      </div>
    </div>
  `;

  const namaPria = hasil.pria?.nama ? hasil.pria.nama.toUpperCase() : 'CALON KELUARGA';
  const namaWanita = hasil.wanita?.nama ? hasil.wanita.nama.toUpperCase() : '';
  const customTitle = `Jagad Jawa — Petung Salaki Rabi ${namaPria}${namaWanita ? ' & ' + namaWanita : ''}`;

  if (typeof window.printLaporan === 'function') {
    window.printLaporan(theme, customTitle);
  } else {
    window.print();
  }
}
